import { useEffect, useMemo, useRef, useState } from "react";

// Local snapshot type for HoverDebugBar diagnostics.
export type LovPointerSnapshot = {
  x: number;
  y: number;
  ts: number; // performance.now()
  type: string; // pointermove/mousemove/pointerdown/etc.
  buttons?: number;
  pointerType?: string;
  pointerId?: number;
  target?: string;
};

interface HoverDebugBarProps {
  enabled?: boolean;
}

type PointerSnapshot = LovPointerSnapshot;

declare global {
  interface Window {
    __lovPointerSnapshot?: PointerSnapshot;
    __lovDragActive?: boolean;
    __lovLastDragEvent?: string;
    __lovLastDragTs?: number;
  }
}

function safeClassName(el: Element): string {
  const anyEl = el as any;
  if (typeof anyEl.className === "string") return anyEl.className;
  if (anyEl.className && typeof anyEl.className.baseVal === "string") return anyEl.className.baseVal;
  return "";
}

function fmt(v: string, max = 60) {
  return v && v.length > max ? `${v.slice(0, max)}…` : v;
}

function shortEl(el: Element | null) {
  if (!el) return "(none)";
  const id = (el as HTMLElement).id ? `#${(el as HTMLElement).id}` : "";
  const cls = safeClassName(el);
  const clsShort = cls ? `.${fmt(cls.trim().split(/\s+/).slice(0, 2).join("."), 40)}` : "";
  return `${el.tagName.toLowerCase()}${id}${clsShort}`;
}

function isInViewport(rect: DOMRect, vw: number, vh: number) {
  return !(rect.right < 0 || rect.bottom < 0 || rect.left > vw || rect.top > vh);
}

export function HoverDebugBar({ enabled = false }: HoverDebugBarProps) {
  const [liveLine, setLiveLine] = useState<string>("Waiting for pointer events…");
  const [scanOutput, setScanOutput] = useState<string>("");
  const [mode, setMode] = useState<"live" | "scan">("live");

  const rafId = useRef<number | null>(null);
  const lastLiveRef = useRef<string>("");
  const lastMoveStateRef = useRef<string>("INIT");
  const lastAutoScanTsRef = useRef<number>(0);
  const modeRef = useRef(mode);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const portalSelector = useMemo(
    () => "[data-radix-portal], [data-radix-popper-content-wrapper]",
    [],
  );

  const runScan = (contextLine: string, auto: boolean) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const viewportArea = Math.max(1, vw * vh);

    const portals = Array.from(document.querySelectorAll(portalSelector));

    const els = Array.from(document.querySelectorAll("body *"));
    const candidates: {
      el: Element;
      rect: DOMRect;
      z: number;
      pe: string;
      pos: string;
      op: string;
      vis: string;
      areaRatio: number;
    }[] = [];

    for (const el of els) {
      const cs = getComputedStyle(el);
      const pos = cs.position;

      // Only likely overlay layers.
      if (pos !== "fixed" && pos !== "absolute" && pos !== "sticky") continue;
      if (cs.pointerEvents === "none") continue;
      if (cs.display === "none" || cs.visibility === "hidden") continue;

      const rect = (el as HTMLElement).getBoundingClientRect();
      if (!isInViewport(rect, vw, vh)) continue;

      const area = Math.max(0, rect.width) * Math.max(0, rect.height);
      const areaRatio = area / viewportArea;
      if (areaRatio < 0.2) continue; // ignore small popovers

      const z = Number.isFinite(parseInt(cs.zIndex)) ? parseInt(cs.zIndex) : 0;
      candidates.push({
        el,
        rect,
        z,
        pe: cs.pointerEvents,
        pos,
        op: cs.opacity,
        vis: cs.visibility,
        areaRatio,
      });
    }

    candidates.sort((a, b) => {
      if (b.z !== a.z) return b.z - a.z;
      return b.areaRatio - a.areaRatio;
    });

    const top = candidates.slice(0, 8);

    const lines: string[] = [];
    lines.push(auto ? "OVERLAY SCAN (AUTO)" : "OVERLAY SCAN (manual)");
    lines.push(contextLine);
    lines.push("---");

    if (top.length === 0) {
      lines.push("No large fixed/absolute elements found (>=20% viewport, pe!=none). ");
    } else {
      top.forEach((c, i) => {
        lines.push(
          `${i + 1}) ${shortEl(c.el)} | pos:${c.pos} pe:${c.pe} z:${c.z} op:${c.op} vis:${c.vis} | rect:${Math.round(
            c.rect.left,
          )},${Math.round(c.rect.top)} ${Math.round(c.rect.width)}x${Math.round(c.rect.height)} | area:${Math.round(
            c.areaRatio * 100,
          )}%`,
        );
      });
    }

    lines.push("---");
    lines.push(`Radix portals: ${portals.length}`);
    portals.slice(0, 6).forEach((p, i) => {
      const cs = getComputedStyle(p);
      lines.push(
        `P${i + 1}) ${shortEl(p)} | pos:${cs.position} pe:${cs.pointerEvents} z:${cs.zIndex} op:${cs.opacity} vis:${cs.visibility}`,
      );
    });

    const output = lines.join("\n");
    setScanOutput(output);

    // Auto-scan should surface immediately without extra clicks.
    if (auto || modeRef.current === "scan") {
      setMode("scan");
    }
  };

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      const snap = window.__lovPointerSnapshot;
      const w = window.innerWidth;
      const h = window.innerHeight;

      const bodyPE = getComputedStyle(document.body).pointerEvents;
      const htmlPE = getComputedStyle(document.documentElement).pointerEvents;
      const portals = document.querySelectorAll(portalSelector).length;

      const dragActive = !!window.__lovDragActive;
      const lastDragEvent = window.__lovLastDragEvent || "-";
      const lastDragAge = window.__lovLastDragTs
        ? Math.max(0, Math.round(performance.now() - window.__lovLastDragTs))
        : null;

      const ageMs = snap ? Math.max(0, Math.round(performance.now() - snap.ts)) : 0;
      const ageBucket = Math.floor(ageMs / 100) * 100;
      const moveState = !snap ? "NO_SNAPSHOT" : ageMs > 400 ? "STALE" : "OK";

      const pt = snap?.pointerType ? ` ptype:${snap.pointerType}` : "";
      const target = snap?.target ? ` tgt:${snap.target}` : "";

      const mouseXY = snap ? `${Math.round(snap.x)},${Math.round(snap.y)} (${snap.type})${pt}${target}` : "-";
      const underMouse = snap ? document.elementFromPoint(snap.x, snap.y) : null;

      // Fixed-point sampling (helps detect a full-page overlay even when mousemove stops)
      const center = document.elementFromPoint(Math.round(w / 2), Math.round(h / 2));

      const dragBit = `drag:${dragActive ? "ON" : "off"}(${lastDragEvent}${
        lastDragAge === null ? "" : ` ${Math.floor(lastDragAge / 100) * 100}ms`
      })`;

      const next = `MOVE:${moveState} age:${ageBucket}ms | ${dragBit} | mouse:${mouseXY} | under:${shortEl(
        underMouse,
      )} | center:${shortEl(center)} | bodyPE:${bodyPE} htmlPE:${htmlPE} | portals:${portals}`;

      // Auto-scan the moment movement goes stale (this correlates strongly with the hover-freeze).
      const prevMove = lastMoveStateRef.current;
      lastMoveStateRef.current = moveState;
      if (prevMove === "OK" && moveState === "STALE") {
        const now = performance.now();
        if (now - lastAutoScanTsRef.current > 1000) {
          lastAutoScanTsRef.current = now;
          // Defer so we don't block RAF; run once per transition.
          window.setTimeout(() => runScan(next, true), 0);
        }
      }

      if (next !== lastLiveRef.current) {
        lastLiveRef.current = next;
        setLiveLine(next);
      }

      rafId.current = window.requestAnimationFrame(tick);
    };

    rafId.current = window.requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafId.current !== null) {
        window.cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [enabled, portalSelector]);

  const copy = async () => {
    const toCopy = mode === "scan" ? scanOutput || liveLine : liveLine;
    try {
      await navigator.clipboard.writeText(toCopy);
    } catch {
      // ignore – clipboard may be blocked by browser permissions
    }
  };

  if (!enabled) return null;

  return (
    <div
      id="hover-debug"
      className="fixed bottom-0 left-0 right-0 z-[99999] bg-destructive text-destructive-foreground px-2 py-2 text-[11px] leading-snug font-mono"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMode("live")}
          className="underline"
          aria-label="Show live view"
        >
          Live
        </button>
        <button
          type="button"
          onClick={() => runScan(liveLine, false)}
          className="underline"
          aria-label="Scan for overlay blockers"
        >
          Scan
        </button>
        <button
          type="button"
          onClick={copy}
          className="underline"
          aria-label="Copy debug text"
        >
          Copy
        </button>
        <span className="opacity-80">(auto-scan triggers when MOVE becomes STALE)</span>
      </div>

      <pre className="mt-1 whitespace-pre-wrap break-words">
        {mode === "scan" ? scanOutput || "(scan empty)" : liveLine}
      </pre>
    </div>
  );
}




